import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

type UIState = {
  sidebarOpen: boolean;
  theme: Theme;
  /** Pedido para abrir um submenu do sidebar; `nonce` distingue pedidos repetidos do mesmo id. */
  sidebarFocus: { id: string; nonce: number } | null;
  focusSidebarSubMenu: (id: string) => void;
  setSidebarOpen: (open: boolean) => void;
  setTheme: (theme: Theme) => void;
};

// Default só se aplica na 1ª visita (sem nada em localStorage ainda) — depois
// disso o valor persistido manda, mesmo trocando de dispositivo/viewport.
// Sem isso, a sidebar (offcanvas no mobile) abriria por cima do conteúdo já
// no primeiro load em telas pequenas.
const DEFAULT_SIDEBAR_OPEN =
  typeof window === 'undefined' || !window.matchMedia('(max-width: 767px)').matches;

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: DEFAULT_SIDEBAR_OPEN,
      theme: 'system',
      sidebarFocus: null,
      focusSidebarSubMenu: (id) =>
        set((s) => ({
          sidebarOpen: true,
          sidebarFocus: { id, nonce: (s.sidebarFocus?.nonce ?? 0) + 1 },
        })),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: 'ui-storage',
      partialize: ({ sidebarOpen, theme }) => ({ sidebarOpen, theme }),
    },
  ),
);

/** Resolve o tema efetivo (`'system'` consulta o SO). Fonte única p/ a classe `.dark` e libs externas que precisem seguir o tema do app. */
export function resolveTheme(theme: Theme): 'light' | 'dark' {
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  return isDark ? 'dark' : 'light';
}

export function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle('dark', resolveTheme(theme) === 'dark');
}
